import React from "react";
import { Image as ImageIcon, Video, FileText, Mic, MapPin, User, Smile, Reply } from "lucide-react";

/**
 * QuotedMessage — the "replying to…" header rendered INSIDE a bubble, above the
 * message body, the way WhatsApp shows a quoted reply.
 *
 * It reads only the denormalised `replyTo*` fields the backend stores on the
 * reply itself, never the original document. That is deliberate: the quoted
 * message frequently isn't in the loaded page (or in the database at all — it
 * may predate the integration or have aged out of the 90-day TTL), and a quote
 * that renders only when the original happens to be on screen would flicker in
 * and out as the user pages through history.
 *
 * Props:
 *   message      — the REPLY, carrying { replyToMessageId, replyToPreview,
 *                  replyToType, replyToDirection }
 *   contactName  — display name of the other party, for attributing an
 *                  inbound original
 *   onJumpTo(id) — optional; when given, the quote is clickable and should
 *                  scroll the original into view. Omitted when the original
 *                  isn't ours to jump to.
 */

const TYPE_ICON = {
  image: ImageIcon,
  sticker: Smile,
  video: Video,
  document: FileText,
  audio: Mic,
  location: MapPin,
  contacts: User,
};

const QuotedMessage = ({ message, contactName = "", onJumpTo }) => {
  const { replyToMessageId, replyToPreview, replyToType, replyToDirection } = message || {};

  // Nothing to quote. A reply whose original we never stored still has neither
  // an id nor a preview, and rendering an empty bar would just be noise.
  if (!replyToMessageId && !replyToPreview) return null;

  const Icon = TYPE_ICON[replyToType];
  const author = replyToDirection === "outbound" ? "You" : (contactName || "Contact");

  /* An empty preview on a quote we DO have an id for means the original was
     deleted from the inbox — the backend blanks the copy so removed text can't
     survive inside someone else's bubble. Say so rather than showing a gap. */
  const unavailable = !replyToPreview;
  const jumpable = typeof onJumpTo === "function" && !!replyToMessageId;

  const body = (
    <>
      <span className="wa-quote__author">{author}</span>
      <span className={`wa-quote__preview${unavailable ? " wa-quote__preview--muted" : ""}`}>
        {Icon && <Icon size={11} className="wa-quote__type-icon" aria-hidden="true" />}
        {unavailable ? "Message unavailable" : replyToPreview}
      </span>
    </>
  );

  if (!jumpable) {
    return <div className="wa-quote wa-quote--static">{body}</div>;
  }

  return (
    <button
      type="button"
      className="wa-quote"
      onClick={(e) => { e.stopPropagation(); onJumpTo(replyToMessageId); }}
      aria-label={`Replying to ${author}. Go to original message.`}
    >
      <Reply size={9} className="wa-quote__jump-hint" aria-hidden="true" />
      {body}
    </button>
  );
};

export default QuotedMessage;
